import { assert, getUniqueId } from "@jugl-web/utils";
import { WithApiRequestMeta } from "../../api-results";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import { ModuleNotificationsApiTag } from "./tags";
import {
  ModuleNotification,
  RawModuleNotificationsResponse,
  SupportedNotificationsModule,
  SupportedNotificationsType,
} from "./types";

export const addModuleNotificationsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [ModuleNotificationsApiTag.ModuleLog],
  });

  const moduleNotificationsApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      getModuleNotifications: builder.query<
        PaginatedResponse<ModuleNotification>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            module: SupportedNotificationsModule;
            boardId?: string;
            flag?: boolean;
          }>
        >
      >({
        query: ({
          entityId,
          module,
          boardId,
          flag,
          meta: _meta,
          ...pageParams
        }) => ({
          url: `/api/auth/entity/${entityId}/logs`,
          params: {
            module,
            board_id: boardId,
            flag,
            ...pageParams,
          },
        }),
        transformResponse: (response: RawModuleNotificationsResponse) => {
          // Unify the response structure
          if (response.data.length === 0) {
            return {
              ...response,
              data: [],
            };
          }

          if ("logs" in response.data[0]) {
            return {
              ...response,
              data: response.data.map((notification) => {
                assert("logs" in notification);

                return {
                  id: getUniqueId(),
                  logs: notification.logs,
                };
              }),
            };
          }

          return {
            ...response,
            data: response.data.map((log) => {
              assert(!("logs" in log));

              return {
                id: getUniqueId(),
                logs: [log],
              };
            }),
          };
        },
      }),

      getModuleNotificationsUnreadIndicator: builder.query<
        { unread: boolean },
        WithApiRequestMeta<{
          entityId: string;
          module: SupportedNotificationsModule;
          boardId?: string;
        }>
      >({
        query: ({ entityId, module, boardId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
          params: {
            type: "module_unread",
            module,
            board_id: boardId,
          },
        }),
      }),

      markModuleNotificationsUnreadIndicatorAsUnread: builder.mutation<
        null,
        WithApiRequestMeta<{
          entityId: string;
          module: SupportedNotificationsModule;
          boardId?: string;
        }>
      >({
        queryFn: () => ({ data: null }),
        onQueryStarted: ({ entityId, module, boardId }, { dispatch }) => {
          dispatch(
            moduleNotificationsApi.util.updateQueryData(
              "getModuleNotificationsUnreadIndicator",
              { entityId, module, boardId },
              (draftState) => {
                draftState.unread = true;
              }
            )
          );
        },
      }),

      markModuleNotificationsUnreadIndicatorAsRead: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          module: SupportedNotificationsModule;
          boardId?: string;
        }>
      >({
        query: ({ entityId, module, boardId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
          method: "POST",
          data: { type: "module_unread", module, board_id: boardId },
        }),
        onQueryStarted: (
          { entityId, module, boardId },
          { dispatch, queryFulfilled }
        ) => {
          const patchResult = dispatch(
            moduleNotificationsApi.util.updateQueryData(
              "getModuleNotificationsUnreadIndicator",
              { entityId, module, boardId },
              (draftState) => {
                draftState.unread = false;
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      markNotificationAsRead: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          logIds: string[];
          boardId?: string;
        }>
      >({
        query: ({ entityId, logIds, boardId }) => ({
          url: `/api/auth/entity/${entityId}/logs`,
          method: "PUT",
          data: { id: logIds, board_id: boardId },
        }),
      }),

      markObjectLevelIndicatorAsRead: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          objectId: string;
          module: SupportedNotificationsModule;
          type: SupportedNotificationsType;
        }>
      >({
        query: ({ entityId, objectId, module, type }) => ({
          url: `/api/auth/entity/${entityId}/logs`,
          method: "PUT",
          data: { id: [objectId], module, type },
        }),
      }),
    }),
    overrideExisting: false,
  });

  return moduleNotificationsApi;
};

export type ModuleNotificationsApi = ReturnType<
  typeof addModuleNotificationsApi
>;
